import React, { useEffect, useState } from 'react';
import {
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  Alert,
  InputAdornment,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Numbers, Inventory, CheckCircle, Warning } from '@mui/icons-material';
import { useBundleCreation } from '../../contexts/BundleCreationContext';
import { calculateMaxPossibleBundles, validateBundleStock, getAggregatedChildSKUs } from '../../utils/inventoryUtils';

const QuantityInput: React.FC = () => {
  const { state, dispatch } = useBundleCreation();
  const [maxPossible, setMaxPossible] = useState<number>(0);
  const [isCalculatingMax, setIsCalculatingMax] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isValidating, setIsValidating] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>(state.quantity.toString());

  // Sync input value when state quantity changes (from external sources)
  useEffect(() => {
    setInputValue(state.quantity.toString());
  }, [state.quantity]);

  // Calculate maximum possible bundles when bundle is selected
  useEffect(() => {
    if (!state.selectedBundleSKU) return;

    const calculateMax = async () => {
      setIsCalculatingMax(true);
      try {
        const maxBundles = await calculateMaxPossibleBundles(state.selectedBundleSKU!.childSKUs);
        setMaxPossible(maxBundles);
      } catch (error) {
        console.error('❌ Error calculating max bundles:', error);
        console.error('Error details:', error);
        setMaxPossible(0);
      } finally {
        setIsCalculatingMax(false);
      }
    };

    calculateMax();
  }, [state.selectedBundleSKU]);

  // Validate stock when quantity or bundle changes
  useEffect(() => {
    if (!state.selectedBundleSKU || state.quantity <= 0) {
      setValidationResult(null);
      return;
    }

    const validateStock = async () => {
      setIsValidating(true);
      try {
        const result = await validateBundleStock(
          state.selectedBundleSKU!.childSKUs,
          state.quantity
        );
        setValidationResult(result);
        dispatch({ type: 'SET_VALIDATION_RESULT', payload: result });
      } catch (error) {
        console.error('❌ Stock validation error:', error);
        console.error('Error validating stock:', error);
        setValidationResult({
          isValid: false,
          errors: ['Error checking stock availability'],
          childSKUAvailability: []
        });
      } finally {
        setIsValidating(false);
      }
    };

    validateStock();
  }, [state.selectedBundleSKU, state.quantity, dispatch]);

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;
    setInputValue(rawValue); // Update local state immediately for smooth editing

    // Update the global state only if it's a valid number
    if (rawValue !== '') {
      const value = parseInt(rawValue) || 0;
      if (value > 0) {
        dispatch({ type: 'SET_QUANTITY', payload: value });
      }
    }
  };

  if (!state.selectedBundleSKU) {
    return (
      <Alert severity="warning">
        Please select a bundle SKU first.
      </Alert>
    );
  }

  const isQuantityValid = state.quantity <= maxPossible && state.quantity > 0;

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Numbers />
        Set Quantity
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter the number of {state.selectedBundleSKU.name} bundles you want to create
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" gutterBottom>
            Selected Bundle: {state.selectedBundleSKU.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Each bundle requires the following child SKUs:
          </Typography>

          <Box sx={{ mt: 2 }}>
            {getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).map((childSKU: any) => (
              <Typography key={childSKU.skuId} variant="body2" sx={{ ml: 2 }}>
                • {childSKU.name}: {childSKU.requiredQuantity} units each
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Maximum Possible Bundles Card */}
      <Card sx={{ mb: 3, bgcolor: maxPossible > 0 ? 'success.50' : 'warning.50' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Inventory color={maxPossible > 0 ? 'success' : 'warning'} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Stock Availability Check
            </Typography>
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: maxPossible > 0 ? 'success.main' : 'warning.main' }}>
                {maxPossible}
              </Typography>
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  Maximum Possible Bundles
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Based on current child SKU inventory
                </Typography>
              </Box>
              {maxPossible > 0 ? (
                <CheckCircle color="success" />
              ) : (
                <Warning color="warning" />
              )}
            </Box>

            {isCalculatingMax ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Calculating maximum possible bundles...
                </Typography>
              </Box>
            ) : maxPossible === 0 ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
                  No bundles can be created - insufficient stock for all child SKUs
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Maximum {maxPossible} bundle{maxPossible !== 1 ? 's' : ''} can be created based on current inventory
                </Typography>
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <TextField
          type="number"
          value={inputValue}
          onChange={handleQuantityChange}
          onBlur={(e) => {
            const value = parseInt(e.target.value) || 1;
            const maxAllowed = maxPossible > 0 ? maxPossible : 1000;
            const finalValue = Math.max(1, Math.min(value, maxAllowed));
            setInputValue(finalValue.toString());
            dispatch({ type: 'SET_QUANTITY', payload: finalValue });
          }}
          label="Quantity to Create"
          inputProps={{
            min: 1,
            max: maxPossible > 0 ? maxPossible : 1000,
            step: 1,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Numbers color="primary" />
              </InputAdornment>
            ),
          }}
          error={state.quantity > maxPossible && state.quantity > 0}
          helperText={
            state.quantity > maxPossible && state.quantity > 0
              ? `Exceeds maximum: ${maxPossible} bundles available`
              : `Enter quantity (max: ${maxPossible} bundles available)`
          }
          sx={{ minWidth: 250 }}
        />

        {maxPossible > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
            <Chip
              label={`Max: ${maxPossible}`}
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => dispatch({ type: 'SET_QUANTITY', payload: maxPossible })}
              sx={{ cursor: 'pointer' }}
            />
            {maxPossible >= 5 && (
              <Chip
                label="Set 5"
                color="secondary"
                variant="outlined"
                size="small"
                onClick={() => dispatch({ type: 'SET_QUANTITY', payload: Math.min(5, maxPossible) })}
                sx={{ cursor: 'pointer' }}
              />
            )}
            {maxPossible >= 10 && (
              <Chip
                label="Set 10"
                color="secondary"
                variant="outlined"
                size="small"
                onClick={() => dispatch({ type: 'SET_QUANTITY', payload: Math.min(10, maxPossible) })}
                sx={{ cursor: 'pointer' }}
              />
            )}
          </Box>
        )}
      </Box>

      {state.quantity > 0 && (
        <Card variant="outlined" sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Numbers color="primary" />
              Creation Summary
            </Typography>

            <Alert
              severity={isQuantityValid ? "info" : "warning"}
              sx={{ mb: 2 }}
              icon={isQuantityValid ? <CheckCircle /> : <Warning />}
            >
              You are about to create <strong>{state.quantity}</strong> bundle(s) of{' '}
              <strong>{state.selectedBundleSKU.name}</strong>
              {!isQuantityValid && state.quantity > maxPossible && (
                <>
                  <br />
                  <Typography variant="body2" sx={{ mt: 1, color: 'warning.dark' }}>
                    ⚠️ This exceeds the maximum possible quantity ({maxPossible}).
                  </Typography>
                </>
              )}
            </Alert>

            <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 1 }}>
              Total Child SKUs Required:
            </Typography>

            {isValidating ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  Validating stock availability...
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {validationResult?.childSKUAvailability?.map((skuAvailability: any) => {
                  return (
                    <Box key={skuAvailability.skuId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="body2">
                        {skuAvailability.name || skuAvailability.skuId}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                          {skuAvailability.requiredQuantity} units needed
                        </Typography>
                        <Chip
                          label={skuAvailability.isAvailable ? 'Available' : 'Insufficient'}
                          color={skuAvailability.isAvailable ? 'success' : 'error'}
                          size="small"
                        />
                      </Box>
                    </Box>
                  );
                }) || getAggregatedChildSKUs(state.selectedBundleSKU.childSKUs).map((sku: any) => (
                  <Box key={sku.skuId} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2">
                      {sku.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {sku.requiredQuantity * state.quantity} units needed
                      </Typography>
                      <Chip
                        label="Checking..."
                        color="default"
                        size="small"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default QuantityInput;